import { Injectable } from '@angular/core';

const AUTH_TOKEN_KEY = 'auth_token';
const AUTH_REFRESH_TOKEN_KEY = 'auth_refresh_token';
const AUTH_USER_KEY = 'auth_user';
const DEV_OTP_KEY = 'dev_otp';
const AUTH_TOKEN_EXPIRES_AT_KEY = 'auth_token_expires_at';

export interface IStoredUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  roles: string[];
  username?: string;
  nationalId?: string;
  serviceCategoryName?: string;
  memberId?: string | null;
  categoryId?: string | null;
  memberName?: string;
  defaultRoleId?: string;
  officeId?: string;
  [key: string]: unknown;
}

@Injectable({ providedIn: 'root' })
export class TokenStorageService {
  getToken(): string | null {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  }

  setToken(token: string): void {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(AUTH_REFRESH_TOKEN_KEY);
  }

  setRefreshToken(refreshToken: string): void {
    localStorage.setItem(AUTH_REFRESH_TOKEN_KEY, refreshToken);
  }

  setSession(token: string, refreshToken: string): void {
    this.setToken(token);
    this.setRefreshToken(refreshToken);
  }

  getUser(): IStoredUser | null {
    const raw = localStorage.getItem(AUTH_USER_KEY);
    if (!raw) {
      return null;
    }

    const user = JSON.parse(raw) as IStoredUser;

    // Remove permissions saved by older clients. Page access is derived from roles.
    if ('permissions' in user) {
      delete user['permissions'];
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
    }

    return user;
  }

  setUser(user: IStoredUser): void {
    const storedUser = { ...user };
    delete storedUser['permissions'];
    localStorage.setItem(AUTH_USER_KEY, JSON.stringify(storedUser));
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /** Stores the absolute time (ms since epoch) the current access token expires at. */
  setTokenExpiresIn(expiresInSeconds: number): void {
    localStorage.setItem(AUTH_TOKEN_EXPIRES_AT_KEY, String(Date.now() + expiresInSeconds * 1000));
  }

  getTokenExpiresAt(): number | null {
    const raw = localStorage.getItem(AUTH_TOKEN_EXPIRES_AT_KEY);
    return raw ? Number(raw) : null;
  }

  clear(): void {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(AUTH_REFRESH_TOKEN_KEY);
    localStorage.removeItem(AUTH_TOKEN_EXPIRES_AT_KEY);
  }

  /** Dev-only convenience: some non-prod backends return the OTP in the response body. */
  setDevOtp(otp: string): void {
    localStorage.setItem(DEV_OTP_KEY, otp);
  }

  getDevOtp(): string {
    const otp = localStorage.getItem(DEV_OTP_KEY) ?? '';
    localStorage.removeItem(DEV_OTP_KEY);
    return otp;
  }
}
